import { useState } from "react";
import { useNavigate, Link as RouterLink, Navigate } from "react-router-dom";
import {
  Box, Button, Center, FormControl, FormLabel, Heading, Input, Stack, Text,
  InputGroup, InputLeftElement, InputRightElement, IconButton, Icon, Link,
} from "@chakra-ui/react";
import { FaUserCircle, FaLock, FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { resolveLoginEmail } from "../config/adminAliases";

// Traduce los mensajes de error de Supabase Auth (en inglés) al español.
function translateAuthError(error) {
  const raw = (error?.message || "").toLowerCase();
  if (raw.includes("invalid login credentials")) return "Correo o contraseña incorrectos.";
  if (raw.includes("email not confirmed")) return "El correo aún no ha sido confirmado.";
  if (raw.includes("user not found")) return "No existe una cuenta con esos datos.";
  if (raw.includes("too many requests") || error?.status === 429)
    return "Demasiados intentos. Espera un momento e inténtalo de nuevo.";
  if (raw.includes("failed to fetch") || raw.includes("network"))
    return "No se pudo conectar. Revisa tu conexión e inténtalo de nuevo.";
  return "No se pudo iniciar sesión. Verifica tus datos e inténtalo de nuevo.";
}

export default function Login() {
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in? Don't show the login form — go straight to the panel.
  if (!loading && user) return <Navigate to="/admin" replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const email = resolveLoginEmail(identifier);
    const { error } = await signIn(email, password);
    setBusy(false);
    if (error) setError(translateAuthError(error));
    // replace: don't leave /login in history, so "back" won't return here.
    else navigate("/admin", { replace: true });
  }

  return (
    <Center minH="100vh" bg="bg.canvas" px={4}>
      <Box
        bg="bg.card"
        border="1px solid"
        borderColor="border.card"
        p={{ base: 6, md: 10 }}
        rounded="2xl"
        w="full"
        maxW="md"
        boxShadow="2xl"
      >
        <Center mb={4}>
          <Box bg="blue.600" rounded="full" w={16} h={16} display="flex" alignItems="center" justifyContent="center">
            <FaUserCircle size={34} color="white" />
          </Box>
        </Center>
        <Heading size="lg" mb={1} textAlign="center" color="text.heading">
          Iniciar sesión
        </Heading>
        <Text fontSize="sm" color="text.muted" textAlign="center" mb={8}>
          Panel de administración
        </Text>

        <form onSubmit={handleSubmit}>
          <Stack spacing={5}>
            <FormControl>
              <FormLabel color="text.primary">Email o usuario</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaUserCircle} color="text.muted" />
                </InputLeftElement>
                <Input
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="ejemplo@mail.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel color="text.primary">Contraseña</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaLock} color="text.muted" />
                </InputLeftElement>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <InputRightElement>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                    onClick={() => setShowPassword((s) => !s)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {error && (
              <Text color="red.400" fontSize="sm">
                {error}
              </Text>
            )}

            <Button type="submit" colorScheme="blue" size="lg" isLoading={busy}>
              Entrar
            </Button>
          </Stack>
        </form>

        <Center mt={6}>
          <Link as={RouterLink} to="/" color="text.muted" fontSize="sm" _hover={{ color: "brand.400" }}>
            <Box as="span" display="inline-flex" alignItems="center" gap={2}>
              <FaArrowLeft size={12} /> Volver al inicio
            </Box>
          </Link>
        </Center>
      </Box>
    </Center>
  );
}
